type AudioCallback = (pcm: ArrayBuffer) => void;

export class PcmCapture {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private speaking = false;
  private speechStartedAt = 0;
  private lastSpeechAt = 0;
  private sensitivity = 35;
  private interruptionProtection = 65;

  constructor(
    private readonly targetSampleRate: number,
    private readonly onAudio: AudioCallback,
    private readonly onSpeechStarted: () => void,
    private readonly onLevel: (level: number) => void,
    private readonly isAgentSpeaking: () => boolean,
  ) {}

  setSensitivity(value: number): void {
    this.sensitivity = Math.max(1, Math.min(100, Math.round(value)));
  }

  setInterruptionProtection(value: number): void {
    this.interruptionProtection = Math.max(1, Math.min(100, Math.round(value)));
  }

  async start(): Promise<void> {
    if (this.context) return;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.context = new AudioContext({ latencyHint: "interactive" });
    const workletUrl = new URL("pcm-capture-worklet.js", document.baseURI);
    await this.context.audioWorklet.addModule(workletUrl.toString());

    this.source = this.context.createMediaStreamSource(this.stream);
    this.worklet = new AudioWorkletNode(this.context, "pcm-capture", {
      processorOptions: { targetSampleRate: this.targetSampleRate },
    });
    this.worklet.port.onmessage = (event: MessageEvent) => {
      const { pcm, rms } = event.data as { pcm: ArrayBuffer; rms: number };
      this.onLevel(Math.min(1, rms * 12));
      this.detectSpeech(rms);
      this.onAudio(pcm);
    };
    this.source.connect(this.worklet);
    this.worklet.connect(this.context.destination);
  }

  private detectSpeech(rms: number): void {
    const now = performance.now();
    // 50% соответствует прежнему порогу около 0.018. На низкой
    // чувствительности нужен заметно более громкий голос, поэтому фон реже
    // принимается за попытку перебить агента.
    const position = (this.sensitivity - 1) / 99;
    const threshold = 0.06 * Math.pow(0.006 / 0.06, position);
    if (rms > threshold) {
      if (!this.speechStartedAt) this.speechStartedAt = now;
      this.lastSpeechAt = now;
      const protectionMs = this.isAgentSpeaking()
        ? 40 + ((this.interruptionProtection - 1) / 99) * 560
        : 35;
      if (!this.speaking && now - this.speechStartedAt >= protectionMs) {
        this.speaking = true;
        this.onSpeechStarted();
      }
      return;
    }

    this.speechStartedAt = 0;
    if (this.speaking && now - this.lastSpeechAt > 700) {
      this.speaking = false;
    }
  }

  async stop(): Promise<void> {
    this.worklet?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    await this.context?.close();
    this.context = null;
    this.stream = null;
    this.source = null;
    this.worklet = null;
    this.speaking = false;
    this.speechStartedAt = 0;
    this.onLevel(0);
  }
}
